<template>
    <Head :title="t('assessments.nominate.pageTitle')" />
    <BaseLayout>
        <BaseLink :href="backHref" class="self-start body-small text-ink-muted hover:text-ink flex items-center gap-1">
            <i class="bi bi-chevron-left" aria-hidden="true"></i> {{ t("assessments.nominate.back") }}
        </BaseLink>

        <div v-if="loading" class="body-small text-ink-muted">{{ t("assessments.nominate.loading") }}</div>
        <div v-else-if="loadError" class="flex flex-col items-start gap-3">
            <BaseNotification theme="error" :text="loadError" hide-dismiss class="self-stretch" />
            <BaseButton theme="neutral" size="sm" @click="loadCycle">{{ t("common.tryAgain") }}</BaseButton>
        </div>
        <template v-else-if="cycle">
            <BasePageHeader :title="headerTitle">
                <template v-if="mode === 'draft'" #eyebrow>
                    <AssessmentEyebrow>{{ t("assessments.nominate.step", { current: formatNumber(1), total: formatNumber(2) }) }}</AssessmentEyebrow>
                </template>
                <template #subtitle>
                    <template v-if="mode === 'draft'">
                        <template v-if="thresholds !== null">
                            <template v-if="cycle.subject_specifies_relationship">{{ t("assessments.nominate.subtitle.subjectCategorizes") }}</template>
                            <template v-else>{{ t("assessments.nominate.subtitle.raterCategorizes") }}</template>
                        </template>
                        <template v-else>{{ t("assessments.nominate.subtitle.minimum", { count: cycle.min_responses }, cycle.min_responses) }}</template>
                    </template>
                    <template v-else-if="mode === 'add-more'">
                        {{ closesAtLabel ? t("assessments.nominate.subtitle.addMoreUntilDate", { date: closesAtLabel }) : t("assessments.nominate.subtitle.addMore") }}
                    </template>
                    <template v-else>{{ t("assessments.nominate.subtitle.closed") }}</template>
                </template>
            </BasePageHeader>

            <AssessmentThresholdsCallout
                v-if="thresholds !== null && mode !== 'read-only'"
                :progress="thresholds"
                :show-invited="showInvitedCounts"
                :show-submitted="showSubmittedCounts" />

            <StatusChip v-if="mode === 'draft'" :state="meetsMin ? 'done' : 'pending'" size="md" class="self-start">
                <template #leading>
                    <i :class="canLaunch ? 'bi bi-check-circle-fill' : 'bi bi-people'" aria-hidden="true"></i>
                </template>
                {{ addedChipLabel }}
            </StatusChip>

            <!-- Existing invites -->
            <BaseCard v-if="invites.length > 0" padding="none">
                <div class="px-4 py-3 border-b border-border body-small-bold text-ink-muted">{{ t("assessments.nominate.invitedHeading") }}</div>
                <ul class="divide-y divide-border">
                    <li v-for="invite in invites" :key="invite.id" class="px-4 py-3 flex items-center gap-3">
                        <PeopleAvatar size="sm" :name="invite.rater.display_name" />
                        <div class="flex-1">
                            <div class="body-small-semibold text-ink">{{ invite.rater.display_name }}</div>
                            <div class="details text-ink-muted">{{ invite.rater.email }}</div>
                            <div v-if="invite.last_reminder_at" class="details text-ink-muted mt-1">
                                {{ t("assessments.nominate.lastReminded", { date: formatInviteNotificationDate(invite.last_reminder_at) }) }}
                            </div>
                            <div v-else-if="invite.invited_at" class="details text-ink-muted mt-1">
                                {{ t("assessments.nominate.requestedReviewAt", { date: formatInviteNotificationDate(invite.invited_at) }) }}
                            </div>
                        </div>
                        <BaseButton
                            v-if="canActOnInvite(cycle, invite) && mode === 'add-more'"
                            type="button"
                            theme="neutral"
                            size="sm"
                            :disabled="resendingId === invite.id || isResendDisabled(invite)"
                            :title="isResendDisabled(invite) ? t('assessments.nominate.resend.cooldownHint') : t('assessments.nominate.resend.hint')"
                            @click="resendInvite(invite)">
                            <i class="bi" :class="resendingId === invite.id ? 'bi-hourglass-split' : resentIds.has(invite.id) ? 'bi-check2' : 'bi-envelope'" aria-hidden="true"></i>
                            {{
                                resendingId === invite.id
                                    ? t("assessments.nominate.resend.sending")
                                    : resentIds.has(invite.id)
                                      ? t("assessments.nominate.resend.sent")
                                      : t("assessments.nominate.resend.action")
                            }}
                        </BaseButton>
                        <AssessmentInviteCopyMenu v-if="invite.invite_token && mode !== 'draft'" :invite="invite" :cycle="cycle" />
                        <StatusChip v-if="invite.state" :state="inviteStateChipState(invite.state)">{{
                            t("assessments.nominate.inviteState", { state: invite.state })
                        }}</StatusChip>
                        <BaseButton
                            v-if="canRemove(invite)"
                            theme="tertiary"
                            size="sm"
                            icon-only
                            :title="t('assessments.nominate.removeRater')"
                            @click="requestRemoveInvite(invite.id)">
                            <i class="bi bi-x-lg" aria-hidden="true"></i>
                        </BaseButton>
                    </li>
                </ul>
            </BaseCard>

            <!-- Add a rater: autocompletes Nadia users, or invite an external email -->
            <BaseCard v-if="mode !== 'read-only'" padding="md">
                <label for="rater-search" class="block">
                    <span class="block body-small-bold text-ink mb-1">{{ t("assessments.nominate.addRater.label") }}</span>
                    <span class="block details text-ink-muted mb-2">{{ t("assessments.nominate.addRater.hint") }}</span>
                    <span class="relative block">
                        <input
                            id="rater-search"
                            v-model="query"
                            :placeholder="t('assessments.nominate.addRater.placeholder')"
                            class="w-full px-3 py-2 border border-control-border rounded-lg outline-none focus:border-control-border-focus body-small"
                            @input="onQueryChange"
                            @focus="onQueryChange"
                            @keydown="handleKeydown" />
                        <ul v-if="options.length > 0" class="absolute z-10 mt-1 w-full bg-surface-raised border border-border rounded-lg shadow-lg max-h-64 overflow-y-auto">
                            <li v-for="(option, index) in options" :key="option.kind === 'user' ? option.rater.uuid : `external:${option.email}`">
                                <button
                                    :ref="(el) => setItemRef(el, index)"
                                    type="button"
                                    class="w-full text-left px-3 py-2 cursor-pointer flex items-center gap-2"
                                    :class="{ 'bg-surface-sunken': index === highlightedIndex }"
                                    @click="selectOption(option)"
                                    @mouseenter="highlightedIndex = index"
                                    @mouseleave="highlightedIndex = -1"
                                    @focus="highlightedIndex = index"
                                    @blur="highlightedIndex = -1">
                                    <template v-if="option.kind === 'user'">
                                        <PeopleAvatar size="xs" :name="option.rater.display_name" />
                                        <div class="flex-1">
                                            <div class="body-small-semibold text-ink">{{ option.rater.display_name }}</div>
                                            <div class="details text-ink-muted">{{ option.rater.email }}</div>
                                        </div>
                                    </template>
                                    <template v-else>
                                        <BaseAvatar size="xs"><i class="bi bi-envelope text-xs" aria-hidden="true"></i></BaseAvatar>
                                        <div class="flex-1">
                                            <div class="body-small-semibold text-ink">
                                                {{
                                                    addingExternal
                                                        ? t("assessments.nominate.addRater.inviting")
                                                        : t("assessments.nominate.addRater.inviteEmail", { email: option.email })
                                                }}
                                            </div>
                                            <div class="details text-ink-muted">{{ t("assessments.nominate.addRater.externalHint") }}</div>
                                        </div>
                                    </template>
                                </button>
                            </li>
                        </ul>
                    </span>
                </label>
            </BaseCard>

            <!-- Personal note (already saved at create-time, but editable here) -->
            <BaseCard v-if="cycle.personal_note" theme="assessment" padding="md">
                <div class="body-small-bold text-ink mb-1">{{ t("assessments.nominate.personalNote.heading") }}</div>
                <p class="body-small text-ink">{{ t("assessments.nominate.personalNote.quoted", { note: cycle.personal_note }) }}</p>
            </BaseCard>

            <BaseNotification v-if="error" theme="error" :text="error" @dismiss="error = null" />

            <div v-if="mode === 'draft'" class="flex items-center justify-between pt-2">
                <div class="body-small text-ink-muted">{{ t("assessments.nominate.nextStep") }}</div>
                <BaseButton theme="primary" :disabled="!canLaunch || launching" @click="onLaunch">
                    {{ launching ? t("assessments.nominate.launching") : t("assessments.nominate.launch") }}
                </BaseButton>
            </div>
            <div v-else class="flex items-center justify-end pt-2">
                <a :href="cycleHref" class="base-btn-primary">{{ t("assessments.nominate.done") }}</a>
            </div>
        </template>
    </BaseLayout>
    <ConfirmationDialog
        v-model:open="isConfirmRemoveDialogOpen"
        :title="t('assessments.nominate.removeDialog.title')"
        :text="t('assessments.nominate.removeDialog.body')"
        :confirm-label="t('assessments.nominate.removeDialog.confirm')"
        @confirm="confirmRemoveInvite" />
</template>

<script lang="ts">
import LeftSidebarLayout from "~vue/layouts/LeftSidebarLayout.vue";
export default { layout: LeftSidebarLayout };
</script>

<script setup lang="ts">
import { Head, router } from "@inertiajs/vue3";
import { useFetch } from "~vue/api";
import type { CycleEnvelopeResponse, CycleForSubject, InviteAnonymized, InviteEnvelopeResponse, SearchRatersResponse, UserMinimal } from "~vue/api/generated/reflections";
import AssessmentEyebrow from "~vue/components/AssessmentEyebrow.vue";
import AssessmentInviteCopyMenu from "~vue/components/AssessmentInviteCopyMenu.vue";
import AssessmentThresholdsCallout from "~vue/components/AssessmentThresholdsCallout.vue";
import BaseAvatar from "~vue/components/BaseAvatar.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseLayout from "~vue/components/BaseLayout.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BasePageHeader from "~vue/components/BasePageHeader.vue";
import ConfirmationDialog from "~vue/components/ConfirmationDialog.vue";
import PeopleAvatar from "~vue/components/PeopleAvatar.vue";
import StatusChip from "~vue/components/StatusChip.vue";
import { useAssessmentInviteResend } from "~vue/composables/useAssessmentInviteResend";
import BaseLink from "~vue/router/BaseLink.vue";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { withLiveNominations } from "~vue/utils/categoryThresholds";
import { formatValidDate } from "~vue/utils/formatDate";
import { formatNumber } from "~vue/utils/formatNumber";
import { isImeComposing } from "~vue/utils/keyboard";
import { logUserInteraction } from "~vue/utils/logUtils";
import { canActOnInvite, inviteStateChipState, isNetworkError } from "~vue/utils/threeSixty";
import type { ComponentPublicInstance } from "vue";
import { computed, nextTick, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const props = defineProps<{
    cycle_id: string;
}>();

const cycle = ref<CycleForSubject | null>(null);
const loading = ref(true);
const loadError = ref<string | null>(null);
const invites = ref<InviteAnonymized[]>([]);
const query = ref("");
const searchResults = ref<UserMinimal[]>([]);
const highlightedIndex = ref(-1);
const itemRefs = ref<HTMLElement[]>([]);
const launching = ref(false);
const error = ref<string | null>(null);
const addingExternal = ref(false);
const isValidEmail = computed(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(query.value.trim()));

// The dropdown is a single list of Nadia-user matches plus, when the input is
// an email with no exact Nadia match, an "invite external" option at the end.
type RaterOption = { kind: "user"; rater: UserMinimal } | { kind: "external"; email: string };

const options = computed<RaterOption[]>(() => {
    const opts: RaterOption[] = searchResults.value.map((rater) => ({ kind: "user", rater }));
    const trimmed = query.value.trim();
    if (isValidEmail.value && !searchResults.value.some((r) => r.email.toLowerCase() === trimmed.toLowerCase())) {
        opts.push({ kind: "external", email: trimmed });
    }
    return opts;
});

function setItemRef(el: Element | ComponentPublicInstance | null, index: number) {
    if (el instanceof HTMLElement) {
        itemRefs.value[index] = el;
    }
}

watch(options, () => {
    highlightedIndex.value = -1;
    itemRefs.value = [];
});

watch(highlightedIndex, async (index) => {
    if (index < 0) {
        return;
    }
    await nextTick();
    itemRefs.value[index]?.scrollIntoView({ block: "nearest" });
});

// Nominate is the resume target for DRAFT cycles, so Back goes to the hub —
// linking to /reflections/360/<id> would just redirect right back here.
const backHref = "/reflections";
function canRemove(invite: InviteAnonymized) {
    return mode.value !== "read-only" && canActOnInvite(cycle.value, invite);
}

const pendingCount = computed(() => invites.value.filter((i) => i.state === "pending").length);
const thresholds = computed(() => {
    const stored = cycle.value?.category_thresholds ?? null;
    return stored === null ? null : withLiveNominations(stored, invites.value);
});
// Invites only carry a category when the subject picks it, so nomination
// progress is unknowable while raters categorize themselves.
const nominationProgress = computed(() => (cycle.value?.subject_specifies_relationship ? thresholds.value : null));
// Invites carry no category until the rater answers when raters categorize
// themselves, so those cycles have no per-category invite count to show.
const showInvitedCounts = computed(() => nominationProgress.value !== null);
// Responses only exist once the cycle is collecting, and they are what unlocks
// the report, so from then on they decide whether a category reads as met.
const showSubmittedCounts = computed(() => mode.value !== "draft");

const meetsMin = computed(() => pendingCount.value >= (cycle.value?.min_responses ?? 0));
// Category thresholds gate the report, not the launch: a subject short of a
// category still launches and keeps inviting while the cycle collects.
const canLaunch = computed(() => (thresholds.value === null ? meetsMin.value : pendingCount.value > 0));

// Only rendered for cycles without category thresholds; the thresholds callout
// carries the progress otherwise.
const addedChipLabel = computed(() => {
    const minimum = cycle.value?.min_responses ?? 0;
    const missing = minimum - pendingCount.value;
    const values = { added: formatNumber(pendingCount.value), minimum: formatNumber(minimum) };
    return missing > 0 ? t("assessments.nominate.addedChipWithMissing", { ...values, missing: formatNumber(missing) }) : t("assessments.nominate.addedChip", values);
});

// `mode` drives all UI variants: DRAFT keeps the launch flow; COLLECTING /
// UNLOCKED cycles allow adding more raters; CLOSED cycles show a read-only
// view.
const mode = computed<"draft" | "add-more" | "read-only">(() => {
    if (cycle.value === null) {
        return "draft";
    }
    if (cycle.value.state === "draft") {
        return "draft";
    }
    if (cycle.value.state === "closed") {
        return "read-only";
    }
    return "add-more";
});

const headerTitle = computed(() => {
    if (mode.value === "draft") {
        return t("assessments.nominate.title.draft");
    }
    return mode.value === "add-more" ? t("assessments.nominate.title.addMore") : t("assessments.nominate.title.readOnly");
});

const closesAtLabel = computed<string | null>(() => {
    if (cycle.value == null || cycle.value.closes_at == null) {
        return null;
    }
    return formatValidDate(cycle.value.closes_at, "month-day") || null;
});

const cycleHref = computed<string>(() => `/reflections/360/${props.cycle_id}`);

const { resendingId, resentIds, resendInvite, isResendDisabled, formatInviteNotificationDate } = useAssessmentInviteResend({
    cycleId: props.cycle_id,
    setError: (message) => {
        error.value = message;
    },
    onInviteUpdated: (inviteId, updatedInvite) => {
        const idx = invites.value.findIndex((invite) => invite.id === inviteId);
        if (idx !== -1) {
            invites.value[idx] = updatedInvite;
        }
    },
});

// Named so the error state can run it again. The failure it most often has to
// recover from is transient, and a full page reload is a heavy way to retry one.
async function loadCycle() {
    loading.value = true;
    loadError.value = null;
    const { data, error: fetchError } = await useFetch(`/v1/reflections/cycles/${props.cycle_id}`).json<CycleEnvelopeResponse>();
    loading.value = false;
    if (fetchError.value) {
        loadError.value = isNetworkError(fetchError.value)
            ? t("reflections.shared.connection.loadFailed")
            : fetchError.value.message || t("assessments.nominate.errors.loadFailed");
        return;
    }
    const loaded = data.value?.cycle;
    if (!loaded) {
        loadError.value = t("assessments.nominate.errors.loadFailed");
        return;
    }
    if (loaded.is_self_assessment_only) {
        router.visit(`/reflections/360/${props.cycle_id}`);
        return;
    }
    cycle.value = loaded;
    invites.value = [...loaded.invites];
    void logUserInteraction(AmplitudeEventNames.ASSESSMENT_NOMINATE_VIEWED, {
        cycle_id: props.cycle_id,
        current_invite_count: invites.value.length,
        min_responses: loaded.min_responses,
    });
}

onMounted(loadCycle);

let searchTimer: ReturnType<typeof setTimeout> | null = null;

function onQueryChange() {
    // The rater errors are all about whatever is in this box — the domain
    // rejection quotes it back — so editing it makes them stale. Without this
    // the banner sat there until the next add attempt.
    error.value = null;
    if (searchTimer) {
        clearTimeout(searchTimer);
    }
    const q = query.value.trim();
    if (q.length < 2) {
        searchResults.value = [];
        return;
    }
    searchTimer = setTimeout(async () => {
        const { data } = await useFetch(`/v1/reflections/raters/search?q=${encodeURIComponent(q)}&cycle_id=${props.cycle_id}`).json<SearchRatersResponse>();
        searchResults.value = data.value?.results ?? [];
    }, 200);
}

function selectOption(option: RaterOption) {
    if (option.kind === "user") {
        void addInvite(option.rater);
    } else {
        void addExternalInvite(option.email);
    }
}

function handleKeydown(e: KeyboardEvent) {
    // Every key here, arrows included, belongs to the IME candidate list while composing.
    if (isImeComposing(e)) {
        return;
    }
    if (options.value.length === 0) {
        return;
    }
    if (e.key === "ArrowDown") {
        e.preventDefault();
        highlightedIndex.value = (highlightedIndex.value + 1) % options.value.length;
    } else if (e.key === "ArrowUp") {
        e.preventDefault();
        highlightedIndex.value = (highlightedIndex.value - 1 + options.value.length) % options.value.length;
    } else if (e.key === "Enter") {
        if (highlightedIndex.value >= 0) {
            e.preventDefault();
            selectOption(options.value[highlightedIndex.value]);
        } else {
            // No explicit highlight: Enter invites an email-only entry as an
            // external rater, but never auto-adds the first name match.
            const external = options.value.find((o) => o.kind === "external");
            if (external) {
                e.preventDefault();
                selectOption(external);
            }
        }
    } else if (e.key === "Escape") {
        e.preventDefault();
        searchResults.value = [];
    }
}

async function addInvite(rater: UserMinimal) {
    error.value = null;
    const { data, error: fetchError } = await useFetch(`/v1/reflections/cycles/${props.cycle_id}/invites`)
        .post(JSON.stringify({ rater_uuid: rater.uuid }), "application/json")
        .json<InviteEnvelopeResponse>();

    if (fetchError.value) {
        error.value = fetchError.value.message || t("assessments.nominate.errors.addFailed");
        return;
    }

    const invite = data.value?.invite;
    if (!invite) {
        error.value = t("assessments.nominate.errors.addFailed");
        return;
    }
    invites.value.push(invite);
    query.value = "";
    searchResults.value = [];
    void logUserInteraction(AmplitudeEventNames.ASSESSMENT_RATER_ADDED, {
        cycle_id: props.cycle_id,
        invite_count_after: invites.value.length,
    });
}

const isConfirmRemoveDialogOpen = ref(false);
const pendingRemoveInviteId = ref<string | null>(null);

function requestRemoveInvite(inviteId: string) {
    pendingRemoveInviteId.value = inviteId;
    isConfirmRemoveDialogOpen.value = true;
}

function confirmRemoveInvite() {
    const inviteId = pendingRemoveInviteId.value;
    pendingRemoveInviteId.value = null;
    if (inviteId) {
        void removeInvite(inviteId);
    }
}

async function removeInvite(inviteId: string) {
    const { error: fetchError } = await useFetch(`/v1/reflections/cycles/${props.cycle_id}/invites/${inviteId}`).delete().json();
    if (fetchError.value) {
        error.value = t("assessments.nominate.errors.removeFailed");
        return;
    }
    invites.value = invites.value.filter((i) => i.id !== inviteId);
    void logUserInteraction(AmplitudeEventNames.ASSESSMENT_RATER_REMOVED, {
        cycle_id: props.cycle_id,
        invite_count_after: invites.value.length,
    });
}

async function addExternalInvite(email: string) {
    if (addingExternal.value) {
        return;
    }
    addingExternal.value = true;
    error.value = null;
    const { data, error: fetchError } = await useFetch(`/v1/reflections/cycles/${props.cycle_id}/external-invites`)
        .post(JSON.stringify({ rater_email: email }), "application/json")
        .json<InviteEnvelopeResponse>();
    addingExternal.value = false;

    if (fetchError.value) {
        error.value = fetchError.value.message || t("assessments.nominate.errors.inviteFailed");
        return;
    }
    const invite = data.value?.invite;
    if (!invite) {
        error.value = t("assessments.nominate.errors.inviteFailed");
        return;
    }
    invites.value.push(invite);
    query.value = "";
    searchResults.value = [];
    void logUserInteraction(AmplitudeEventNames.ASSESSMENT_RATER_ADDED, {
        cycle_id: props.cycle_id,
        invite_count_after: invites.value.length,
        is_external: true,
    });
}

async function onLaunch() {
    if (!canLaunch.value || launching.value || mode.value !== "draft") {
        return;
    }
    launching.value = true;
    error.value = null;
    try {
        const { error: fetchError } = await useFetch(`/v1/reflections/cycles/${props.cycle_id}/launch`).post().json<CycleEnvelopeResponse>();
        if (fetchError.value) {
            error.value = fetchError.value.message || t("assessments.nominate.errors.launchFailed");
            return;
        }
        const next = cycle.value?.self_assess_enabled ? `/reflections/360/${props.cycle_id}/self` : `/reflections/360/${props.cycle_id}`;
        router.visit(next);
    } finally {
        launching.value = false;
    }
}
</script>
